import { useEffect, useRef, useState } from 'react';
import {
  getIframePoolOfferItems,
  getIframePoolOfferOdds,
  getPoolOffer,
  listAvailableCategories,
  type ApiClient,
  type AvailableCategory,
  type IframePoolItem,
  type IframePoolOfferOdds,
  type PoolOfferDetail,
} from '../../../modules/api';
import { useLeaveForLab } from '../../../modules/customPack/useLeaveForLab';

export interface PackDataState {
  offer: PoolOfferDetail | null;
  categories: AvailableCategory[];
  odds: IframePoolOfferOdds | null;
  maxForPurchaseCount: number | undefined;
  bgImageUrl: string | null;
  potentialHits: IframePoolItem[];
  potentialHitsLoading: boolean;
  loading: boolean;
  error: string | null;
  /** The pack is a custom pack and the page is on its way to the lab: show no pack UI meanwhile. */
  leavingForLab: boolean;
}

/**
 * Parallel fetch for the pack-detail page — identical to the classic
 * template's `usePackData`, kept as a separate file so the modern template
 * owns its own data hook and can diverge (e.g. carousel pre-fetch) later.
 */
export function usePackData(client: ApiClient, gameId: string | null): PackDataState {
  const [offer, setOffer] = useState<PoolOfferDetail | null>(null);
  const [categories, setCategories] = useState<AvailableCategory[]>([]);
  const [odds, setOdds] = useState<IframePoolOfferOdds | null>(null);
  // Odds already fetched this visit, per pack: swiping back to one shows its
  // figures at once instead of waiting on the network again.
  const oddsCache = useRef(new Map<string, IframePoolOfferOdds>());
  // Same for the pack's pool, which "What's inside" lists.
  const hitsCache = useRef(new Map<string, IframePoolItem[]>());
  const [potentialHits, setPotentialHits] = useState<IframePoolItem[]>([]);
  const [potentialHitsLoading, setPotentialHitsLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { leavingForLab, leaveIfCustomPack } = useLeaveForLab();

  useEffect(() => {
    let cancelled = false;
    void listAvailableCategories(client).then((cats) => {
      if (!cancelled) setCategories(cats);
    });
    return () => {
      cancelled = true;
    };
  }, [client]);

  useEffect(() => {
    if (gameId === null) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    // Not cleared on a swipe: the Max Pull block keeps the last pack's
    // figures until this one's land, so only the numbers change — clearing
    // flashed the skeleton for a frame on every swipe (partner Loom
    // 2026-10-13). The skeleton is the first load's only.
    const cached = oddsCache.current.get(gameId);
    if (cached) setOdds(cached);
    const cachedHits = hitsCache.current.get(gameId);
    setPotentialHits(cachedHits ?? []);
    setPotentialHitsLoading(!cachedHits);

    getPoolOffer(client, gameId)
      .then((o) => {
        if (cancelled || leaveIfCustomPack(o)) return;
        setOffer(o);
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : 'Failed to load pack';
        setError(message);
        setLoading(false);
      });

    getIframePoolOfferOdds(client, gameId)
      .then((o) => {
        oddsCache.current.set(gameId, o);
        if (!cancelled) setOdds(o);
      })
      .catch(() => {
        // Odds are optional — but the last pack's must not stay up as this one's
        if (!cancelled) setOdds(null);
      });

    getIframePoolOfferItems(client, gameId)
      .then((page) => {
        hitsCache.current.set(gameId, page.items);
        if (cancelled) return;
        setPotentialHits(page.items);
        setPotentialHitsLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setPotentialHitsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [client, gameId, leaveIfCustomPack]);

  return {
    offer,
    categories,
    odds,
    maxForPurchaseCount: odds ? odds.maxForPurchaseCount : undefined,
    bgImageUrl: odds?.bgImageUrl ?? null,
    potentialHits,
    potentialHitsLoading,
    loading,
    error,
    leavingForLab,
  };
}
